import { 
  collection, 
  query, 
  getDocs,
  collectionGroup,
  orderBy,
  limit,
  Timestamp,
  doc, getDoc, setDoc
} from "firebase/firestore";
import { db } from "./authService";
import { AdminAnalytics, UserStats, DailyStats, ADMIN_EMAILS, User, AdvertisingModalConfig } from "../types";

/**
 * Check if current user is an admin
 */
export const isAdmin = (user: User | null): boolean => {
  return user !== null && ADMIN_EMAILS.includes(user.email);
};

/**
 * Get all analytics data for the admin dashboard
 */
export const getAdminAnalytics = async (): Promise<AdminAnalytics> => {
  const now = Date.now();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - 7);
  weekStart.setHours(0, 0, 0, 0);
  
  const monthStart = new Date();
  monthStart.setDate(monthStart.getDate() - 30);
  monthStart.setHours(0, 0, 0, 0);

  try {
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const totalUsers = usersSnapshot.size;

    // Get all history items using collectionGroup
    const historyQuery = query(
      collectionGroup(db, 'history'),
      orderBy('timestamp', 'desc'),
      limit(10000)
    );
    const historySnapshot = await getDocs(historyQuery);
    
    const totalAnalyses = historySnapshot.size;
    let analysesToday = 0;
    let analysesThisWeek = 0;
    let analysesThisMonth = 0;
    
    // Track stats per user and daily
    const userStatsMap = new Map<string, { count: number; lastActive: number }>();
    const dailyStatsMap = new Map<string, number>();

    historySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const timestamp = data.timestamp instanceof Timestamp 
        ? data.timestamp.toMillis() 
        : (typeof data.timestamp === 'number' ? data.timestamp : now);
      
      const itemDate = new Date(timestamp);
      
      // Count by time period
      if (timestamp >= todayStart.getTime()) {
        analysesToday++;
      }
      if (timestamp >= weekStart.getTime()) {
        analysesThisWeek++;
      }
      if (timestamp >= monthStart.getTime()) {
        analysesThisMonth++;
      }
      
      // Extract userId from doc ref path
      const pathParts = doc.ref.path.split('/');
      const userId = pathParts.length >= 2 ? pathParts[1] : 'unknown';
      
      // Track user stats
      const existing = userStatsMap.get(userId);
      if (existing) {
        existing.count++;
        existing.lastActive = Math.max(existing.lastActive, timestamp);
      } else {
        userStatsMap.set(userId, { count: 1, lastActive: timestamp });
      }
      
      // Track daily stats (last 30 days)
      if (timestamp >= monthStart.getTime()) {
        const dateKey = itemDate.toISOString().split('T')[0];
        dailyStatsMap.set(dateKey, (dailyStatsMap.get(dateKey) || 0) + 1);
      }
    });

    // Build top users list
    const topUsers: UserStats[] = [];
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;
      const stats = userStatsMap.get(userId);
      
      if (stats) {
        topUsers.push({
          userId,
          email: userData.email || 'Unknown',
          name: userData.name || userData.displayName || 'Unknown',
          analysisCount: stats.count,
          lastActive: stats.lastActive
        });
      }
    }
    
    // Sort by analysis count descending
    topUsers.sort((a, b) => b.analysisCount - a.analysisCount);

    // Build daily stats for the last 30 days
    const dailyStats: DailyStats[] = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      dailyStats.push({
        date: dateKey,
        analysisCount: dailyStatsMap.get(dateKey) || 0
      });
    }

    return {
      totalUsers,
      totalAnalyses,
      analysesToday,
      analysesThisWeek,
      analysesThisMonth,
      topUsers: topUsers.slice(0, 10),
      dailyStats
    };
  } catch (error) {
    console.error('Error fetching admin analytics:', error);
    throw error;
  }
};

/**
 * Get the advertising modal configuration
 */
export const getModalConfig = async (): Promise<AdvertisingModalConfig> => {
  const defaultConfig: AdvertisingModalConfig = {
    enabled: false,
    title: 'Descubre Nuestros Servicios',
    message: 'Potenciá tu comunicación en salud con nuestras herramientas profesionales.',
    linkText: 'Conocer más',
    linkUrl: 'https://masempatia.com',
    frequency: 'once_per_session',
    buttonText: 'Continuar al resultado',
    timeoutSeconds: 5
  };

  try {
    const configRef = doc(db, 'config', 'advertisingModal');
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      return { ...defaultConfig, ...configSnap.data() } as AdvertisingModalConfig;
    }
    
    return defaultConfig;
  } catch (error) {
    console.error('Error fetching modal config:', error);
    return defaultConfig;
  }
};

/**
 * Update the advertising modal configuration (admin only)
 */
export const updateModalConfig = async (config: AdvertisingModalConfig): Promise<void> => {
  try {
    const configRef = doc(db, 'config', 'advertisingModal');
    await setDoc(configRef, config);
  } catch (error) {
    console.error('Error updating modal config:', error);
    throw error;
  }
};
